import { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2, UploadCloud, Play, Trash2, RefreshCw, AlertTriangle, Plus, ScanSearch, UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Progress } from "../ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import ResultsTable from "./ResultsTable";

const STROKE = 1.5;

// NOTE (4g.3): this component shows NO monetary figures anywhere. The daily AI
// cost cap is still enforced server-side on every run — it is expressed to the
// recruiter purely in CVs. Spend lives on the admin-only Settings > Usage page.

function RunGate({ readiness, onRun, onRunLimited, running }) {
  if (!readiness) return null;
  const { can_run, over_limit, max_cvs_now, cvs_needing_match, requirement_count, reason } = readiness;
  return (
    <div className="rounded-lg border border-emerald-800/20 bg-emerald-50/50 p-4" data-testid="run-gate">
      <div className="flex items-center gap-2 text-[13px] font-medium text-emerald-900">
        <ScanSearch className="h-4 w-4" strokeWidth={STROKE} />
        {cvs_needing_match} CV{cvs_needing_match === 1 ? "" : "s"} ready to screen
      </div>
      <p className="mt-1 text-[12.5px] text-ink-600">
        Each CV is checked against {requirement_count} active requirement{requirement_count === 1 ? "" : "s"}.
      </p>
      {!can_run && (
        <p className="mt-2 flex items-center gap-1.5 text-[12.5px] text-amber-700" data-testid="run-gate-blocked">
          <AlertTriangle className="h-3.5 w-3.5" strokeWidth={STROKE} /> {reason}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {can_run && (
          <button
            onClick={onRun}
            disabled={running}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-800 px-4 py-2 text-[13px] font-medium text-white hover:bg-emerald-900 disabled:opacity-50"
            data-testid="run-screening-button"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> : <Play className="h-4 w-4" strokeWidth={STROKE} />}
            Run screening
          </button>
        )}
        {over_limit && max_cvs_now > 0 && (
          <button
            onClick={() => onRunLimited(max_cvs_now)}
            disabled={running}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-800 px-4 py-2 text-[13px] font-medium text-white hover:bg-emerald-900 disabled:opacity-50"
            data-testid="run-limited-button"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> : <Play className="h-4 w-4" strokeWidth={STROKE} />}
            Screen first {max_cvs_now} now
          </button>
        )}
      </div>
    </div>
  );
}

function PromoteDialog({ row, roleId, onClose, onDone }) {
  const stem = (row.original_filename || "")
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const [name, setName] = useState(row.candidate_name || stem);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Enter the candidate's name.");
      return;
    }
    setBusy(true);
    try {
      const body = { name: name.trim() };
      if (email.trim()) body.email = email.trim();
      const { data } = await api.post(`/screening-cvs/${row.screening_cv_id}/promote`, body);
      toast.success(`${data.name} added to the pipeline.`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add to the pipeline");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="promote-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px]">Add to pipeline</DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Creates a candidate on this role and attaches this CV as their current résumé.
            The name comes from the filename — correct it before saving.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="hp-section-label">Candidate name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-md border border-ink-200 px-3 py-2 text-[13px] focus:border-emerald-800/40 focus:outline-none"
              placeholder="Full name"
              data-testid="promote-name-input"
            />
          </div>
          <div>
            <label className="hp-section-label">Email (optional)</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-ink-200 px-3 py-2 text-[13px] focus:border-emerald-800/40 focus:outline-none"
              placeholder="name@company.com"
              data-testid="promote-email-input"
            />
          </div>
          <p className="text-[11.5px] text-ink-400">
            Source file: {row.original_filename}
          </p>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="rounded-md border border-ink-200 px-3 py-2 text-[12.5px] text-ink-600 hover:border-ink-300"
            data-testid="promote-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-800 px-4 py-2 text-[12.5px] font-medium text-white hover:bg-emerald-900 disabled:opacity-60"
            data-testid="promote-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> : <UserPlus className="h-4 w-4" strokeWidth={STROKE} />}
            Add to pipeline
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadZone({ batchId, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/screening-batches/${batchId}/cvs`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const failed = data.failed?.length || 0;
      toast.success(`Added ${data.searchable} readable CV${data.searchable === 1 ? "" : "s"}${failed ? `, ${failed} could not be read` : ""}.`);
      onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <label
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
      className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-ink-300 px-4 py-10 text-center hover:border-emerald-800/40"
      data-testid="cv-upload-zone"
    >
      {busy ? <Loader2 className="h-6 w-6 animate-spin text-emerald-800" strokeWidth={STROKE} /> : <UploadCloud className="h-6 w-6 text-ink-400" strokeWidth={STROKE} />}
      <span className="text-[13px] font-medium text-ink-700">
        {busy ? "Uploading…" : "Drag & drop CVs, or click to choose"}
      </span>
      <span className="text-[11.5px] text-ink-400">PDF, DOC, DOCX · up to 100 per batch · 10MB each</span>
      <span className="text-[11px] text-ink-400">You can run more batches against the same JD, so 100 isn&rsquo;t the ceiling for a role.</span>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.doc"
        className="hidden"
        onChange={(e) => upload(e.target.files)}
        data-testid="cv-upload-input"
      />
    </label>
  );
}

export default function ScreeningBatchPanel({ jobDescriptionId, roleId, activeRequirementCount, canEdit, canDelete }) {
  const [batches, setBatches] = useState([]);
  const [batch, setBatch] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [promoteRow, setPromoteRow] = useState(null);
  const pollRef = useRef(null);

  const loadBatches = useCallback(async (selectId) => {
    const { data } = await api.get(`/roles/${roleId}/screening-batches`);
    const forJd = data.filter((b) => b.job_description_id === jobDescriptionId);
    setBatches(forJd);
    const chosen = selectId ? forJd.find((b) => b.screening_batch_id === selectId) : forJd[0];
    setBatch(chosen || null);
    return chosen || null;
  }, [roleId, jobDescriptionId]);

  const loadReadiness = useCallback(async (batchId) => {
    try {
      const { data } = await api.get(`/screening-batches/${batchId}/run-readiness`);
      setReadiness(data);
    } catch { setReadiness(null); }
  }, []);

  const loadResults = useCallback(async (batchId) => {
    try {
      const { data } = await api.get(`/screening-batches/${batchId}/results`);
      setResults(data);
    } catch { setResults(null); }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const b = await loadBatches();
      if (b) {
        await Promise.all([loadReadiness(b.screening_batch_id), loadResults(b.screening_batch_id)]);
      }
      setLoading(false);
    })();
    return () => clearInterval(pollRef.current);
  }, [loadBatches, loadReadiness, loadResults]);

  // Poll while running.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (batch?.status === "running") {
      pollRef.current = setInterval(async () => {
        const { data } = await api.get(`/screening-batches/${batch.screening_batch_id}`);
        setBatch(data);
        if (["completed", "partial", "failed"].includes(data.status)) {
          clearInterval(pollRef.current);
          await Promise.all([loadReadiness(data.screening_batch_id), loadResults(data.screening_batch_id)]);
        }
      }, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [batch?.status, batch?.screening_batch_id, loadReadiness, loadResults]);

  const createBatch = async () => {
    try {
      const { data } = await api.post(`/screening-batches`, { job_description_id: jobDescriptionId });
      await loadBatches(data.screening_batch_id);
      setResults(null);
      setReadiness(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start a batch");
    }
  };

  const run = async ({ resume = false, limit = null } = {}) => {
    setRunning(true);
    try {
      const path = `/screening-batches/${batch.screening_batch_id}/${resume ? "resume" : "run"}`;
      const { data } = await api.post(limit ? `${path}?limit=${limit}` : path);
      setBatch(data);
      toast.success("Screening started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start screening");
    } finally {
      setRunning(false);
    }
  };

  const refreshCurrent = async () => {
    if (!batch) return;
    const { data } = await api.get(`/screening-batches/${batch.screening_batch_id}`);
    setBatch(data);
    await Promise.all([loadReadiness(data.screening_batch_id), loadResults(data.screening_batch_id)]);
  };

  const removeBatch = async () => {
    if (!window.confirm("Delete this batch and all its CVs? This cannot be undone.")) return;
    try {
      await api.delete(`/screening-batches/${batch.screening_batch_id}`);
      toast.success("Batch deleted");
      setResults(null); setReadiness(null);
      await loadBatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-ink-400" strokeWidth={STROKE} /></div>;
  }

  if (activeRequirementCount === 0) {
    return (
      <div className="hp-card p-5 text-[13px] text-ink-500" data-testid="screening-need-requirements">
        Confirm at least one active requirement above before screening CVs.
      </div>
    );
  }

  const doneCount = (results?.rows || []).filter((r) => r.requirements_needing_match === 0).length;
  const readableCount = batch?.searchable_cv_count || 0;

  return (
    <div className="space-y-4" data-testid="screening-batch-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-[16px] font-medium text-ink-900">
          <ScanSearch className="h-4 w-4 text-emerald-800" strokeWidth={STROKE} /> Screen CVs
        </h2>
        <div className="flex items-center gap-2">
          {batches.length > 0 && (
            <select
              value={batch?.screening_batch_id || ""}
              onChange={(e) => { const b = batches.find((x) => x.screening_batch_id === e.target.value); setBatch(b); loadReadiness(b.screening_batch_id); loadResults(b.screening_batch_id); }}
              className="rounded-md border border-ink-200 px-2 py-1 text-[12.5px]"
              data-testid="batch-selector"
            >
              {batches.map((b) => (
                <option key={b.screening_batch_id} value={b.screening_batch_id}>
                  {`${b.title} · ${new Date(b.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} · ${b.cv_count} CV${b.cv_count === 1 ? "" : "s"} · ${b.status}`}
                </option>
              ))}
            </select>
          )}
          {canEdit && (
            <button onClick={createBatch} className="inline-flex items-center gap-1.5 rounded-md border border-ink-200 bg-white px-3 py-1.5 text-[12.5px] font-medium text-ink-700 hover:border-emerald-800/40 hover:text-emerald-900" data-testid="new-batch-button">
              <Plus className="h-3.5 w-3.5" strokeWidth={STROKE} /> New batch
            </button>
          )}
        </div>
      </div>

      {!batch ? (
        <div className="hp-card p-8 text-center" data-testid="no-batch">
          <p className="text-[13px] text-ink-500">No screening batch yet for this JD.</p>
          {canEdit && (
            <button onClick={createBatch} className="mt-3 inline-flex items-center gap-2 rounded-md bg-emerald-800 px-4 py-2 text-[13px] font-medium text-white hover:bg-emerald-900" data-testid="start-batch-button">
              <Plus className="h-4 w-4" strokeWidth={STROKE} /> Start a screening batch
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-ink-200 bg-ink-50/50 px-3 py-2 text-[12.5px] text-ink-600" data-testid="batch-summary">
            <span><strong>{batch.cv_count}</strong> CVs</span>
            <span>·</span>
            <span>{batch.searchable_cv_count} readable</span>
            {batch.failed_count > 0 && <><span>·</span><span className="text-amber-700">{batch.failed_count} unreadable</span></>}
            <span>·</span>
            <span className="capitalize">{batch.status}</span>
            {canDelete && (
              <button onClick={removeBatch} className="ml-auto inline-flex items-center gap-1 text-[12px] text-ink-400 hover:text-red-600" data-testid="delete-batch-button">
                <Trash2 className="h-3.5 w-3.5" strokeWidth={STROKE} /> Delete batch
              </button>
            )}
          </div>

          {canEdit && batch.status !== "running" && (
            <UploadZone batchId={batch.screening_batch_id} onUploaded={refreshCurrent} />
          )}

          {batch.status === "running" ? (
            <div className="hp-card p-5" data-testid="batch-progress">
              <div className="mb-2 flex items-center justify-between text-[13px] text-ink-700">
                <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-emerald-800" strokeWidth={STROKE} /> Matching CVs…</span>
                <span>Processed {batch.processed_count} of {batch.processing_total}</span>
              </div>
              <Progress value={batch.processing_total ? (batch.processed_count / batch.processing_total) * 100 : 0} />
            </div>
          ) : (
            <>
              {batch.status === "failed" && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[12.5px] text-red-900" data-testid="batch-failed-note">
                  <span className="flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" strokeWidth={STROKE} />
                    {batch.error || "Screening didn't finish."}
                  </span>
                  {canEdit && (
                    <button onClick={() => run({ resume: true })} className="inline-flex items-center gap-1 font-medium text-red-900 hover:underline" data-testid="retry-batch-button">
                      <RefreshCw className="h-3.5 w-3.5" strokeWidth={STROKE} /> Retry
                    </button>
                  )}
                </div>
              )}
              {batch.status === "partial" && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900" data-testid="partial-note">
                  <span className="flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" strokeWidth={STROKE} />
                    {batch.stopped_at_cap
                      ? `Screening paused for today. ${doneCount} of ${readableCount} CVs are done and saved. Resume tomorrow.`
                      : `${doneCount} of ${readableCount} CVs are done and saved. Some didn't finish — resume to process the rest.`}
                  </span>
                  {canEdit && (
                    <button onClick={() => run({ resume: true })} className="inline-flex items-center gap-1 font-medium text-amber-900 hover:underline" data-testid="resume-button">
                      <RefreshCw className="h-3.5 w-3.5" strokeWidth={STROKE} /> Resume screening
                    </button>
                  )}
                </div>
              )}
              {results && results.requirements_needing_match_total > 0 && batch.searchable_cv_count > 0 && (
                <div className="rounded-md border border-emerald-800/20 bg-emerald-50/40 px-3 py-2 text-[12.5px] text-emerald-900" data-testid="needs-rematch-note">
                  {results.requirements_needing_match_total} requirement-check{results.requirements_needing_match_total === 1 ? "" : "s"} need matching
                  (new or unprocessed). Only those are matched — nothing already checked is redone.
                </div>
              )}
              {canEdit && readiness && readiness.cvs_needing_match > 0
                && !["partial", "failed"].includes(batch.status) && (
                <RunGate
                  readiness={readiness}
                  onRun={() => run({})}
                  onRunLimited={(n) => run({ limit: n })}
                  running={running}
                />
              )}
              {results && results.rows.length + (results.failed?.length || 0) > 0 && (
                <ResultsTable
                  results={results}
                  canEdit={canEdit}
                  onRefresh={refreshCurrent}
                  onPromote={(row) => setPromoteRow(row)}
                />
              )}
              {results && results.rows.length === 0 && (results.failed?.length || 0) === 0 && batch.cv_count === 0 && (
                <div className="hp-card p-6 text-center text-[13px] text-ink-400" data-testid="batch-no-cvs">
                  Upload CVs above to screen them against your requirements.
                </div>
              )}
            </>
          )}
        </>
      )}

      {promoteRow && (
        <PromoteDialog
          row={promoteRow}
          roleId={roleId}
          onClose={() => setPromoteRow(null)}
          onDone={refreshCurrent}
        />
      )}
    </div>
  );
}
